import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  XCircle,
  Upload,
  Download,
  FileText,
  AlertTriangle,
  Eye,
  Search,
  Calendar,
  DollarSign,
  User,
  Clock,
  ArrowLeft
} from 'lucide-react';
import { fundingAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';
import { confirmToast } from '../../utils/sonnerDialogs';

const PaymentManagement = () => {
  const navigate = useNavigate();
  const { logout, getDisplayName } = useAuth();
  const queryClient = useQueryClient();

  const [selectedTab, setSelectedTab] = useState('upcoming');
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [showRentasModal, setShowRentasModal] = useState(false);
  const [showProofModal, setShowProofModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [showCsvPreview, setShowCsvPreview] = useState(false);
  const [isParsingCsv, setIsParsingCsv] = useState(false);

  // Filters
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Mock data for demonstration
  const mockPayments = [
    {
      id: 1,
      bondName: 'Malaysia Infrastructure Bond 2024',
      issuer: 'Malaysia Infrastructure Corp',
      paymentDate: '2024-12-20',
      paymentType: 'Coupon',
      totalAmount: 12500000,
      status: 'Funding Pending',
      investorCount: 245,
      fundingRequest: {
        amount: 12500000,
        requestDate: '2024-12-15',
        proofDocument: 'funding_proof_20241215.pdf',
        bankReference: 'TT20241215001'
      },
      investors: [
        { id: 1, name: 'Pension Fund Malaysia', walletAddress: '0x1234...5678', amount: 2500000 },
        { id: 2, name: 'Investment Fund ABC', walletAddress: '0x2345...6789', amount: 1875000 },
        { id: 3, name: 'Individual Investor John', walletAddress: '0x3456...7890', amount: 625000 }
      ]
    },
    {
      id: 2,
      bondName: 'Green Sukuk Series 3',
      issuer: 'Green Energy Holdings',
      paymentDate: '2024-12-22',
      paymentType: 'Profit',
      totalAmount: 7125000,
      status: 'Ready for Payment',
      investorCount: 156,
      fundingRequest: {
        amount: 7125000,
        requestDate: '2024-12-17',
        confirmedDate: '2024-12-18',
        confirmed: true
      },
      investors: [
        { id: 1, name: 'Islamic Bank Malaysia', walletAddress: '0x4567...8901', amount: 2137500 },
        { id: 2, name: 'Shariah Fund Corp', walletAddress: '0x5678...9012', amount: 1425000 },
        { id: 3, name: 'Retirement Fund Ltd', walletAddress: '0x6789...0123', amount: 712500 }
      ]
    },
    {
      id: 3,
      bondName: 'Tech Development Bond',
      issuer: 'Tech Innovation Ltd',
      paymentDate: '2024-12-25',
      paymentType: 'Coupon',
      totalAmount: 4500000,
      status: 'RENTAS File Generated',
      investorCount: 89,
      rentasFile: {
        filename: 'RENTAS_TECH_20241225.xml',
        generatedDate: '2024-12-24',
        downloadUrl: '#'
      },
      investors: [
        { id: 1, name: 'Tech Investment Fund', walletAddress: '0x7890...1234', amount: 1350000 },
        { id: 2, name: 'Innovation Capital', walletAddress: '0x8901...2345', amount: 900000 },
        { id: 3, name: 'Growth Fund Asia', walletAddress: '0x9012...3456', amount: 675000 }
      ]
    },
    {
      id: 4,
      bondName: 'Infrastructure Bond 2022-A',
      issuer: 'Infrastructure Holdings',
      paymentDate: '2024-12-18',
      paymentType: 'Coupon',
      totalAmount: 8750000,
      status: 'Paid',
      proofOfPayment: {
        filename: 'payment_confirmation_20241218.pdf',
        uploadDate: '2024-12-18',
        downloadUrl: '#'
      },
      investors: [
        { id: 1, name: 'National Pension Fund', walletAddress: '0x0123...4567', amount: 2625000 },
        { id: 2, name: 'Infrastructure Fund', walletAddress: '0x1234...5678', amount: 1750000 }
      ]
    }
  ];

  const paymentHistory = [
    {
      id: 1,
      bondName: 'Government Bond 2023-A',
      issuer: 'Ministry of Finance',
      paymentDate: '2024-11-15',
      paymentType: 'Coupon',
      totalAmount: 15000000,
      status: 'Paid',
      proofDocument: 'payment_proof_20241115.pdf'
    },
    {
      id: 2,
      bondName: 'Corporate Sukuk 2023-B',
      issuer: 'ABC Corporation',
      paymentDate: '2024-10-20',
      paymentType: 'Profit',
      totalAmount: 9500000,
      status: 'Paid',
      proofDocument: 'payment_proof_20241020.pdf'
    }
  ];

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Scheduled': { color: 'gray', icon: Calendar },
      'Funding Pending': { color: 'yellow', icon: Clock },
      'Ready for Payment': { color: 'green', icon: CheckCircle },
      'RENTAS File Generated': { color: 'blue', icon: FileText },
      'Allocation Uploaded': { color: 'purple', icon: Upload },
      'Paid': { color: 'green', icon: CheckCircle },
      'Overdue': { color: 'red', icon: AlertTriangle }
    };

    const config = statusConfig[status] || { color: 'gray', icon: Clock };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getFilteredPayments = () => {
    let payments = selectedTab === 'history' ? paymentHistory : mockPayments;

    return payments.filter(payment => {
      const matchesSearch = payment.bondName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        payment.issuer.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesType = paymentTypeFilter === 'all' || payment.paymentType === paymentTypeFilter;
      const matchesStatus = statusFilter === 'all' || payment.status === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  };

  const handleConfirmFunding = (action) => {
    // Simulate API call
    console.log(`${action} funding for payment ${selectedPayment.id}`);
    if (action === 'reject' && !rejectionReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }
    setShowFundingModal(false);
    setRejectionReason('');
    setSelectedPayment(null);
  };

  const handleGenerateRentas = () => {
    // Simulate RENTAS file generation
    console.log(`Generating RENTAS file for payment ${selectedPayment.id}`);
    setShowRentasModal(false);
    setSelectedPayment(null);
  };

  // Function to parse CSV file
  const parseCsvFile = (file) => {
    setIsParsingCsv(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        const lines = csvText.split('\n').filter(line => line.trim());

        if (lines.length === 0) {
          alert('CSV file is empty');
          setIsParsingCsv(false);
          return;
        }

        // Parse headers (first row)
        const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));

        // Parse data rows
        const rows = lines.slice(1).map((line, index) => {
          const values = line.split(',').map(value => value.trim().replace(/"/g, ''));
          const row = { rowNumber: index + 1 };
          headers.forEach((header, colIndex) => {
            row[header] = values[colIndex] || '';
          });
          return row;
        });

        const parsedData = {
          headers,
          rows,
          totalRows: rows.length,
          totalColumns: headers.length,
          fileName: file.name,
          fileSize: file.size
        };

        console.log('CSV parsed successfully:', parsedData);
        setCsvData(parsedData);
        setShowCsvPreview(true);
        setIsParsingCsv(false);
        console.log('CSV preview should show now');

      } catch (error) {
        console.error('Error parsing CSV:', error);
        alert(`❌ Error parsing CSV file: ${error.message}\n\nPlease check:\n• File is a valid CSV format\n• File is not corrupted\n• File has proper headers and data`);
        setIsParsingCsv(false);
        setCsvData(null);
        setShowCsvPreview(false);
      }
    };

    reader.onerror = () => {
      alert('❌ Error reading file. Please try again or select a different file.');
      setIsParsingCsv(false);
      setCsvData(null);
      setShowCsvPreview(false);
    };

    reader.readAsText(file);
  };

  // Function to handle file selection and parsing
  const handleFileSelect = (file) => {
    console.log('File selected:', file);
    setUploadedFile(file);

    if (file && file.name.toLowerCase().endsWith('.csv')) {
      console.log('CSV file detected, parsing...');
      parseCsvFile(file);
    }
  };

  // Function to confirm CSV upload and update status
  const handleConfirmCsvUpload = async () => {
    if (!csvData || !selectedPayment) return;

    // Create allocation data for AllocationPreview page
    const syncToAllocationPreview = () => {
      try {
        // Get existing allocations from AllocationPreview
        const existingAllocations = JSON.parse(localStorage.getItem('uploadedAllocations') || '[]');

        // Transform CSV data into allocation format
        const allocationData = {
          id: selectedPayment.id,
          bondData: {
            bondName: selectedPayment.bondName,
            issuer: selectedPayment.issuer,
            productType: selectedPayment.paymentType === 'Profit' ? 'Sukuk' : 'Conventional Bonds',
            nominalAmount: selectedPayment.totalAmount,
            issueDate: selectedPayment.paymentDate,
            couponRate: selectedPayment.paymentType === 'Coupon' ? 5.25 : null,
            profitRate: selectedPayment.paymentType === 'Profit' ? 5.50 : null,
            maturityDate: calculateMaturityDate(selectedPayment.paymentDate),
            bondType: selectedPayment.paymentType === 'Profit' ? 'Islamic' : 'Conventional'
          },
          uploadDate: new Date().toISOString().split('T')[0],
          status: 'Allocation Uploaded',
          csvFileName: csvData.fileName,
          csvData: csvData,
          allocations: transformCsvToAllocations(csvData),
          totalAllocated: calculateTotalAllocation(csvData)
        };

        // Check if allocation already exists
        const existingIndex = existingAllocations.findIndex(alloc => alloc.id === selectedPayment.id);

        if (existingIndex >= 0) {
          // Update existing allocation
          existingAllocations[existingIndex] = allocationData;
        } else {
          // Add new allocation
          existingAllocations.push(allocationData);
        }

        // Save to localStorage
        localStorage.setItem('uploadedAllocations', JSON.stringify(existingAllocations));

        console.log('✅ Allocation data synced to AllocationPreview:', allocationData);
        return allocationData;

      } catch (error) {
        console.error('Error syncing to AllocationPreview:', error);
        return null;
      }
    };

    // Helper function to calculate maturity date (default 2 years from issue date)
    const calculateMaturityDate = (issueDate) => {
      const maturityDate = new Date(issueDate);
      maturityDate.setFullYear(maturityDate.getFullYear() + 2);
      return maturityDate.toISOString().split('T')[0];
    };

    // Helper function to transform CSV data to allocation format
    const transformCsvToAllocations = (csvData) => {
      return csvData.rows.map((row, index) => {
        // Try to extract relevant fields from CSV
        const investorName = row['Investor Name'] || row['InvestorName'] || row['Name'] || `Investor ${index + 1}`;
        const investorId = row['Investor ID'] || row['InvestorID'] || row['ID'] || `INV${String(index + 1).padStart(3, '0')}`;
        const tokenAmount = parseFloat(row['Bond Amount'] || row['Token Amount'] || row['Amount'] || row['Allocation'] || '0') || 0;
        const walletAddress = row['Wallet Address'] || row['Wallet'] || row['Address'] || `0x${Math.random().toString(16).substr(2, 40)}`;

        // Calculate allocation percentage
        const totalAmount = csvData.rows.reduce((sum, r) => sum + (parseFloat(r['Bond Amount'] || r['Token Amount'] || r['Amount'] || r['Allocation'] || '0') || 0), 0);
        const allocationPercentage = totalAmount > 0 ? (tokenAmount / totalAmount) * 100 : 0;

        return {
          investorId,
          investorName,
          tokenAmount,
          whitelistedWallet: walletAddress,
          allocationPercentage,
          paymentStatus: 'Pending',
          paymentReference: null
        };
      }).filter(allocation => allocation.tokenAmount > 0); // Filter out zero allocations
    };

    // Helper function to calculate total allocation
    const calculateTotalAllocation = (csvData) => {
      return csvData.rows.reduce((sum, row) => {
        const amount = parseFloat(row['Bond Amount'] || row['Token Amount'] || row['Amount'] || row['Allocation'] || '0') || 0;
        return sum + amount;
      }, 0);
    };

    // Sync to AllocationPreview
    const allocationData = syncToAllocationPreview();

    if (allocationData) {
      const shouldNavigate = await confirmToast(`✅ Success!\n\nCSV file "${csvData.fileName}" has been uploaded successfully.\n\n📋 Summary:\n• Status updated to: Allocation Uploaded\n• Rows processed: ${csvData.totalRows}\n• Columns: ${csvData.totalColumns}\n• Investors allocated: ${allocationData.allocations.length}\n• Total allocation: ${formatCurrency(allocationData.totalAllocated)}\n\n🎯 Next Step: View and finalize allocations in the Allocation Preview page\n\nWould you like to go to Allocation Preview now?`, { confirmLabel: 'Go to Allocation Preview' });

      if (shouldNavigate) {
        // Close modals and navigate
        setShowCsvPreview(false);
        setShowProofModal(false);
        setCsvData(null);
        setUploadedFile(null);
        setSelectedPayment(null);
        navigate('/facility-agent/allocation-preview');
        return;
      }
    } else {
      alert(`✅ CSV Uploaded!\n\nCSV file "${csvData.fileName}" has been uploaded successfully.\n\nStatus updated to: Allocation Uploaded\nRows processed: ${csvData.totalRows}\nColumns: ${csvData.totalColumns}`);
    }

    // Close modals and reset state
    setShowCsvPreview(false);
    setShowProofModal(false);
    setCsvData(null);
    setUploadedFile(null);
    setSelectedPayment(null);
  };

  const handleUploadProof = () => {
    if (!uploadedFile) {
      alert('Please select a file to upload');
      return;
    }
    // Simulate file upload
    console.log(`Uploading proof for payment ${selectedPayment.id}`, uploadedFile);
    setShowProofModal(false);
    setUploadedFile(null);
    setSelectedPayment(null);
  };

  const tabs = [
    { id: 'upcoming', name: 'Upcoming Payments', count: mockPayments.filter(p => ['Scheduled', 'Funding Pending', 'Ready for Payment'].includes(p.status)).length },
    { id: 'processing', name: 'Processing', count: mockPayments.filter(p => p.status === 'RENTAS File Generated').length },
    { id: 'completed', name: 'Completed', count: mockPayments.filter(p => p.status === 'Paid').length },
    { id: 'history', name: 'Payment History', count: paymentHistory.length }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Payment Management</h1>
              <p className="mt-2 text-sm text-gray-600">Manage interest/profit payments, funding confirmations, and RENTAS integration</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setSelectedTab(tab.id)}
                  className={`py-2 px-1 border-b-2 font-medium text-sm whitespace-nowrap ${selectedTab === tab.id
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                >
                  {tab.name}
                  {tab.count > 0 && (
                    <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${selectedTab === tab.id ? 'bg-primary-100 text-primary-600' : 'bg-gray-100 text-gray-600'
                      }`}>
                      {tab.count}
                    </span>
                  )}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Search Payments
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by bond name or issuer..."
                    className="pl-10 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>

              {/* Payment Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Type
                </label>
                <select
                  value={paymentTypeFilter}
                  onChange={(e) => setPaymentTypeFilter(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Types</option>
                  <option value="Coupon">Coupon</option>
                  <option value="Profit">Profit</option>
                  <option value="Principal">Principal</option>
                </select>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="all">All Status</option>
                  <option value="Scheduled">Scheduled</option>
                  <option value="Funding Pending">Funding Pending</option>
                  <option value="Ready for Payment">Ready for Payment</option>
                  <option value="RENTAS File Generated">RENTAS File Generated</option>
                  <option value="Paid">Paid</option>
                  <option value="Overdue">Overdue</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Payment List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedTab === 'history' ? 'Payment History' : 'Payment List'} ({getFilteredPayments().length})
              </h3>
            </div>

            <div className="space-y-4">
              {getFilteredPayments().map((payment) => (
                <div key={payment.id} className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-lg font-medium text-gray-900">
                          {payment.bondName}
                        </h4>
                        {getStatusBadge(payment.status)}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                        <div>
                          <p className="text-sm text-gray-500">Issuer</p>
                          <div className="flex items-center mt-1">
                            <User className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm font-medium text-gray-900">{payment.issuer}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Payment Date</p>
                          <div className="flex items-center mt-1">
                            <Calendar className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm font-medium text-gray-900">{formatDate(payment.paymentDate)}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Total Amount</p>
                          <div className="flex items-center mt-1">
                            <DollarSign className="h-4 w-4 text-gray-400 mr-1" />
                            <p className="text-sm font-medium text-gray-900">{formatCurrency(payment.totalAmount)}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Payment Type</p>
                          <p className="text-sm font-medium text-gray-900 mt-1">{payment.paymentType}</p>
                        </div>
                      </div>

                      {payment.investorCount && (
                        <div className="mb-4">
                          <p className="text-sm text-gray-500">
                            {payment.investorCount} investors eligible for payment
                          </p>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            setSelectedPayment(payment);
                            setShowDetailModal(true);
                          }}
                          className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          View Details
                        </button>

                        {payment.status === 'Funding Pending' && (
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowFundingModal(true);
                            }}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Confirm Funding
                          </button>
                        )}

                        {payment.status === 'Ready for Payment' && (
                          <button
                            onClick={() => {
                              setSelectedPayment(payment);
                              setShowRentasModal(true);
                            }}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                          >
                            <FileText className="h-4 w-4 mr-1" />
                            Generate RENTAS File
                          </button>
                        )}

                        {payment.status === 'RENTAS File Generated' && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedPayment(payment);
                                setShowProofModal(true);
                              }}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                            >
                              <Upload className="h-4 w-4 mr-1" />
                              Upload Proof
                            </button>
                            {payment.rentasFile && (
                              <a
                                href={payment.rentasFile.downloadUrl}
                                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Download RENTAS File
                              </a>
                            )}
                          </>
                        )}

                        {payment.status === 'Paid' && payment.proofOfPayment && (
                          <a
                            href={payment.proofOfPayment.downloadUrl}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Download Proof
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              {getFilteredPayments().length === 0 && (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No payments found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No payments match your current filter criteria.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Payment Detail Modal */}
      {showDetailModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">Payment Details</h3>
                <button
                  onClick={() => setShowDetailModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Bond Information</h4>
                  <dl className="space-y-2">
                    <div>
                      <dt className="text-xs text-gray-500">Bond Name</dt>
                      <dd className="text-sm text-gray-900">{selectedPayment.bondName}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Issuer</dt>
                      <dd className="text-sm text-gray-900">{selectedPayment.issuer}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Payment Date</dt>
                      <dd className="text-sm text-gray-900">{formatDate(selectedPayment.paymentDate)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Payment Type</dt>
                      <dd className="text-sm text-gray-900">{selectedPayment.paymentType}</dd>
                    </div>
                    <div>
                      <dt className="text-xs text-gray-500">Total Amount</dt>
                      <dd className="text-sm text-gray-900">{formatCurrency(selectedPayment.totalAmount)}</dd>
                    </div>
                  </dl>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900 mb-3">Investor Breakdown</h4>
                  <div className="max-h-40 overflow-y-auto">
                    {selectedPayment.investors?.map((investor) => (
                      <div key={investor.id} className="flex justify-between py-2 border-b border-gray-100">
                        <div>
                          <p className="text-xs font-medium text-gray-900">{investor.name}</p>
                          <p className="text-xs text-gray-500">{investor.walletAddress}</p>
                        </div>
                        <p className="text-xs font-medium text-gray-900">{formatCurrency(investor.amount)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Funding Confirmation Modal */}
      {showFundingModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Funding Confirmation</h3>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Bond:</strong> {selectedPayment.bondName}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Amount:</strong> {formatCurrency(selectedPayment.totalAmount)}
                </p>
                {selectedPayment.fundingRequest && (
                  <div className="mt-3 p-3 bg-gray-50 rounded">
                    <p className="text-xs text-gray-500">Funding Request Details:</p>
                    <p className="text-sm">Amount: {formatCurrency(selectedPayment.fundingRequest.amount)}</p>
                    <p className="text-sm">Request Date: {formatDate(selectedPayment.fundingRequest.requestDate)}</p>
                    {selectedPayment.fundingRequest.bankReference && (
                      <p className="text-sm">Bank Reference: {selectedPayment.fundingRequest.bankReference}</p>
                    )}
                  </div>
                )}
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Rejection Reason (if rejecting)
                </label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  rows={3}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter reason for rejection..."
                />
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowFundingModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleConfirmFunding('reject')}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                  Reject Funding
                </button>
                <button
                  onClick={() => handleConfirmFunding('confirm')}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Confirm Funding
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RENTAS Generation Modal */}
      {showRentasModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Generate RENTAS File</h3>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Bond:</strong> {selectedPayment.bondName}
                </p>
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Payment Date:</strong> {formatDate(selectedPayment.paymentDate)}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Total Amount:</strong> {formatCurrency(selectedPayment.totalAmount)}
                </p>

                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-blue-800">
                    This will generate a payment instruction file for the RENTAS system containing
                    payment details for {selectedPayment.investorCount} investors.
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowRentasModal(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleGenerateRentas}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Generate File
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Proof Upload Modal */}
      {showProofModal && selectedPayment && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Payment Proof</h3>

              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Bond:</strong> {selectedPayment.bondName}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  <strong>Amount:</strong> {formatCurrency(selectedPayment.totalAmount)}
                </p>

                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload RENTAS Confirmation/Proof or Allocation CSV
                </label>
                <input
                  type="file"
                  accept=".pdf,.xml,.csv"
                  onChange={(e) => {
                    console.log('File input onChange triggered:', e.target.files[0]);
                    if (e.target.files && e.target.files[0]) {
                      handleFileSelect(e.target.files[0]);
                    }
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
                {uploadedFile && (
                  <div className="mt-2">
                    <p className="text-sm text-gray-600">
                      ✅ Selected: <span className="font-medium">{uploadedFile.name}</span>
                    </p>
                    <p className="text-xs text-gray-500">
                      Size: {(uploadedFile.size / 1024).toFixed(2)} KB
                    </p>
                    {uploadedFile.name.toLowerCase().endsWith('.csv') && (
                      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <div className="flex items-center">
                          <FileText className="h-4 w-4 text-blue-600 mr-2" />
                          <p className="text-sm text-blue-700 font-medium">
                            {isParsingCsv ? '⏳ Parsing CSV file...' :
                              csvData ? '✅ CSV parsed successfully!' :
                                '📄 CSV file detected. Click "Parse & Preview CSV" to view data.'}
                          </p>
                        </div>
                        {csvData && (
                          <div className="mt-2 text-xs text-blue-600">
                            📊 {csvData.totalRows} rows, {csvData.totalColumns} columns
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowProofModal(false);
                    setUploadedFile(null);
                    setCsvData(null);
                    setShowCsvPreview(false);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
                {uploadedFile && uploadedFile.name.toLowerCase().endsWith('.csv') && (
                  <button
                    onClick={() => {
                      console.log('Preview CSV clicked, csvData:', csvData);
                      if (csvData) {
                        setShowCsvPreview(true);
                      } else {
                        console.log('No CSV data, parsing file...');
                        parseCsvFile(uploadedFile);
                      }
                    }}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    {csvData ? 'Preview CSV' : 'Parse & Preview CSV'}
                  </button>
                )}
                {uploadedFile && !uploadedFile.name.toLowerCase().endsWith('.csv') && (
                  <button
                    onClick={handleUploadProof}
                    className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors"
                  >
                    Upload Proof
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV Preview Modal */}
      {showCsvPreview && csvData && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">CSV File Preview</h3>
                <button
                  onClick={() => setShowCsvPreview(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              {/* File Information */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-700">File Name:</span>
                    <p className="text-gray-900">{csvData.fileName}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">File Size:</span>
                    <p className="text-gray-900">{(csvData.fileSize / 1024).toFixed(2)} KB</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Rows:</span>
                    <p className="text-gray-900">{csvData.totalRows}</p>
                  </div>
                  <div>
                    <span className="font-medium text-gray-700">Total Columns:</span>
                    <p className="text-gray-900">{csvData.totalColumns}</p>
                  </div>
                </div>
              </div>

              {/* CSV Data Table */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto max-h-96">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Row #
                        </th>
                        {csvData.headers.map((header, index) => (
                          <th
                            key={index}
                            className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                          >
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {csvData.rows.slice(0, 50).map((row, rowIndex) => (
                        <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                          <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                            {row.rowNumber}
                          </td>
                          {csvData.headers.map((header, colIndex) => (
                            <td
                              key={colIndex}
                              className="px-3 py-2 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate"
                              title={row[header]}
                            >
                              {row[header]}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {csvData.totalRows > 50 && (
                  <div className="bg-gray-50 px-4 py-3 text-sm text-gray-500 border-t">
                    Showing first 50 rows of {csvData.totalRows} total rows
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => setShowCsvPreview(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Close Preview
                </button>
                <button
                  onClick={() => {
                    console.log('Confirm Upload clicked, csvData:', csvData, 'selectedPayment:', selectedPayment);
                    handleConfirmCsvUpload();
                  }}
                  disabled={!csvData || !selectedPayment}
                  className={`px-4 py-2 rounded-md transition-colors ${csvData && selectedPayment
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                  {!csvData ? 'No CSV Data' : !selectedPayment ? 'No Payment Selected' : 'Confirm Upload'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}


    </div>
  );
};

export default PaymentManagement; 
